import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"
import MuxPlayer from "@mux/mux-player-react"
import { Play, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import emmaPortrait from "@/images/emma.webp"

/**
 * Props from GuidedVideos#to_props (app/services/guided_videos.rb). The server
 * sends nothing until the video has a playback id and every `at`.
 */
export type GuidedVideoProps = {
  title: string
  /** A PUBLIC Mux playback id. No token is ever sent (see DriverTour). */
  playbackId: string
  points: { at: number; target: string | null; point: string }[]
}

// ---------------------------------------------------------------------------
// Whether a panel is open. The panel belongs to the page, but the room for it
// comes from the layout: AppLayout reads this to move the page left (or, on a
// phone, down) while it's open.

type PanelState = { open: boolean; setOpen: (open: boolean) => void }

const GuidePanelContext = createContext<PanelState>({ open: false, setOpen: () => {} })

export function GuidePanelProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const value = useMemo(() => ({ open, setOpen }), [open])
  return <GuidePanelContext.Provider value={value}>{children}</GuidePanelContext.Provider>
}

export function useGuidePanel() {
  return useContext(GuidePanelContext)
}

/** Classes for the page column while a panel is open. Matches the panel's w-96 and max-h-[46vh]. */
export const GUIDE_PUSH = "lg:mr-96 max-md:pt-[46vh]"

// ---------------------------------------------------------------------------
// Outlining. Done on the DOM, by `data-guide` name, rather than through React
// state like the driver tour: a screen marks its parts once, like a label, and
// needs no wiring to the video. The outline is decorative; it repeats what
// Emma is saying, so it carries no ARIA.

const LIT = ["guide-lit", "guide-lit-inset"]

function clearOutline() {
  document.querySelectorAll(".guide-lit").forEach((el) => el.classList.remove(...LIT))
}

function outline(target: string | null) {
  clearOutline()
  if (!target) return
  // The first match: on a list of rows, "row" means the first one.
  const el = document.querySelector<HTMLElement>(`[data-guide="${CSS.escape(target)}"]`)
  if (!el) return // not on the page (an empty list): outline nothing, scroll nowhere

  el.classList.add("guide-lit")
  if (clipped(el)) el.classList.add("guide-lit-inset")
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  // On a phone the video is pinned over the top of the screen, so "centre" put
  // the part half under it. There it goes just below the video instead
  // (.guide-lit's scroll-margin-top).
  const phone = window.matchMedia("(max-width: 767px)").matches
  // A part taller than the screen (a whole list) is shown from its top: centred,
  // it opened somewhere in the middle, wherever that fell for this carrier.
  const tall = el.getBoundingClientRect().height > window.innerHeight * 0.8
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: phone || tall ? "start" : "center" })
}

// Would something around it cut the outline off? The outline sits outside the
// part, and a box that clips (a Card's rounded corners, a scroller) shows only
// some of its sides. Chartermarks learned this on a table heading; it asks the
// page rather than keeping a list, because the next clipping rule written
// wouldn't be on the list.
function clipped(el: HTMLElement) {
  const reach = 9
  const r = el.getBoundingClientRect()
  for (let box = el.parentElement; box && box !== document.body; box = box.parentElement) {
    const style = getComputedStyle(box)
    if (style.overflowX === "visible" && style.overflowY === "visible") continue
    const b = box.getBoundingClientRect()
    if (r.left - reach < b.left || r.right + reach > b.right || r.top - reach < b.top || r.bottom + reach > b.bottom) {
      return true
    }
  }
  return false
}

// ---------------------------------------------------------------------------

/**
 * A guided help video: Emma's face as the invitation, and pressed, a panel on
 * the right with her video at the top and her key points under it. The point
 * being spoken is marked, pressing one jumps the video there, and the part of
 * the page she's talking about is outlined and scrolled into view.
 *
 * Lessons kept from the driver tour:
 * - **Started by a press.** Browsers block autoplay with sound, and the press
 *   is consent too. Nothing loads from Mux before it.
 * - **Closable, never hidden for good.** The tour's "Hide this" was mis-clicked
 *   within a minute and there was no way back. The invitation stays put.
 * - **A point lasts until the next starts**: recomputed on seek, kept on
 *   pause, cleared at the end and on close.
 * - **Captions on**, for people without headphones.
 * - **The video's ground matches its white backdrop**, or the frame reads as a
 *   box around the picture.
 */
export function GuidedVideo({ video, className }: { video: GuidedVideoProps; className?: string }) {
  const { open, setOpen } = useGuidePanel()
  const [current, setCurrent] = useState<number | null>(null)
  const playerRef = useRef<React.ComponentRef<typeof MuxPlayer>>(null)
  const openerRef = useRef<HTMLButtonElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLOListElement>(null)
  const lastIndex = useRef<number | null>(null)

  // The point being spoken: the last whose start has passed.
  const indexAt = useCallback(
    (seconds: number) => {
      let found: number | null = null
      video.points.forEach((point, i) => {
        if (seconds >= point.at) found = i
      })
      return found
    },
    [video.points],
  )

  const sync = useCallback(() => {
    const index = indexAt(playerRef.current?.currentTime ?? 0)
    if (index === lastIndex.current) return // timeupdate fires several times a second
    lastIndex.current = index
    setCurrent(index)
    outline(index === null ? null : video.points[index].target)
  }, [indexAt, video.points])

  const reset = useCallback(() => {
    lastIndex.current = null
    setCurrent(null)
    clearOutline()
  }, [])

  const close = useCallback(() => {
    playerRef.current?.pause()
    setOpen(false)
    reset()
    openerRef.current?.focus()
  }, [reset, setOpen])

  // Leaving the page (an Inertia visit unmounts this) mustn't leave the layout
  // pushed aside or a part outlined.
  useEffect(
    () => () => {
      setOpen(false)
      clearOutline()
    },
    [setOpen],
  )

  useEffect(() => {
    if (!open) return
    closeRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open, close])

  // The point being spoken stays in sight in the list. Scrolled on the list
  // itself rather than with scrollIntoView, which would move the page too, and
  // two smooth scrolls at once (this and the outline's) cancel each other in
  // Chrome (Chartermarks).
  useEffect(() => {
    const list = listRef.current
    if (current === null || !list) return
    const item = list.children[current] as HTMLElement | undefined
    if (!item || item.offsetParent === null) return // folded away on a phone
    const box = list.getBoundingClientRect()
    const point = item.getBoundingClientRect()
    if (point.bottom > box.bottom) list.scrollTop += point.bottom - box.bottom
    else if (point.top < box.top) list.scrollTop -= box.top - point.top
  }, [current])

  function seek(index: number) {
    const player = playerRef.current
    if (!player) return
    player.currentTime = video.points[index].at
    player.play()?.catch(() => {})
  }

  const total = video.points.length

  return (
    <>
      <button
        ref={openerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        className={cn(
          "flex w-full max-w-md items-center gap-3 rounded-(--radius-md) border border-border bg-card p-2.5 pr-4 text-left transition-colors hover:border-primary/50 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none",
          className,
        )}
      >
        {/* Emma's face is the invitation: a person offering to show you round,
            not a media control. The play badge says it's a video. */}
        <span className="relative shrink-0">
          <img src={emmaPortrait} alt="" width={48} height={48} className="h-12 w-12 rounded-full object-cover" />
          <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-card">
            <Play className="h-3 w-3" aria-hidden="true" />
          </span>
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium">{video.title}</span>
          <span className="block text-sm text-muted-foreground">
            A short video with Emma. She points things out as she goes.
          </span>
        </span>
      </button>

      {open &&
        createPortal(
          // Not a dialog: the page stays usable beside it, which is the point.
          <aside
            aria-label={video.title}
            className="fixed inset-x-0 top-0 z-40 flex max-h-[46vh] flex-col gap-2 border-b border-border bg-card px-3 py-2 text-card-foreground shadow-lg md:inset-x-auto md:right-0 md:h-svh md:max-h-none md:w-96 md:gap-3 md:border-b-0 md:border-l md:p-4"
          >
            <div className="flex shrink-0 items-center justify-between gap-3">
              <p className="text-sm font-semibold">{video.title}</p>
              <Button ref={closeRef} type="button" variant="ghost" size="icon" onClick={close} aria-label="Close the video">
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>

            {/* Square, cropping the recording's empty white sides: at 16:9 Emma
                fills a third of the frame and her ring stair-steps when it's
                shrunk that far (Chartermarks, 2026-10-10). On a phone it gives
                up height before the point under it does. */}
            <div className="min-h-0 shrink overflow-hidden rounded-md bg-media-surface md:shrink-0">
              <MuxPlayer
                ref={playerRef}
                playbackId={video.playbackId}
                streamType="on-demand"
                autoPlay
                // 720p at least, or she's fuzzy while the player climbs from
                // its lowest rendition; at most, or shrinking 1080p this far
                // stair-steps her ring.
                minResolution="720p"
                maxResolution="720p"
                accentColor="var(--color-primary)"
                className="mx-auto block aspect-square max-h-[30vh] w-full md:max-h-none"
                style={{
                  backgroundColor: "var(--color-media-surface)",
                  "--media-object-fit": "cover",
                }}
                onTimeUpdate={sync}
                onSeeked={sync}
                onEnded={reset}
              />
            </div>

            <ol ref={listRef} className="grid shrink-0 content-start gap-0.5 md:min-h-0 md:shrink md:overflow-y-auto">
              {video.points.map((point, index) => (
                // On a phone only the point being spoken shows, and says where
                // it is ("3 of 8"). The number is written, not counted by CSS,
                // because a counter skips the hidden ones.
                <li key={index} className={cn(current !== index && "max-md:hidden")}>
                  <button
                    type="button"
                    onClick={() => seek(index)}
                    aria-current={current === index ? "step" : undefined}
                    className="flex w-full gap-2.5 rounded-md px-2.5 py-2 text-left text-sm leading-snug text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-[current=step]:bg-accent aria-[current=step]:font-semibold aria-[current=step]:text-foreground motion-reduce:transition-none"
                  >
                    <span className="shrink-0 tabular-nums">
                      {index + 1}
                      <span className="md:hidden"> of {total}</span>
                      <span className="max-md:hidden">.</span>
                    </span>
                    <span>{point.point}</span>
                  </button>
                </li>
              ))}
            </ol>
          </aside>,
          document.body,
        )}
    </>
  )
}
